import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Props as TranslationViewSegmentRowProps } from "./TranslationViewSegmentRow";
import { PaginatorOptions, TranslationMemory } from "./types";
import { useGetAuthQuery } from "../api/authApi";
import {
  setDjangoToastOpen,
  NotificationAppearance,
} from "../api/djangoToastSlice";
import { addTranslationMemory } from "../api/translationMemoryApi";
import { Spinner } from "../utils/Spinner";
import { store } from "../utils/store";
import { useSegmentation } from "./SegmentationHook";
import { useSegmentRegex } from "./SegmentRegexHook";
import { TranslationViewAdminPanel } from "./TranslationViewAdminPanel";
import { TranslationViewSegmentTable } from "./TranslationViewSegmentTable";
import { TranslationViewTranslationMemoryLookups } from "./TranslationViewTranslationMemoryLookups";
import {
  segmentTextsFocusHandler,
  changeWordCountInput,
  focusSegment,
  getNextFocusableSegment,
  remainFocusOnSegment,
} from "./utils";

export const getInitialDisabledSegmentTagRuleIds = (
  selector: string
): number[] => {
  const element = document.getElementById(selector) as HTMLInputElement;
  if (!element) return [];
  const value = element.value;
  if (!value) return [];
  try {
    return JSON.parse(value) as number[];
  } catch (error) {
    console.error("Failed to get initial disabled tag rule ids", error);
    return [];
  }
};

export const getInitialSegmentationPaginatorOptions = (
  selector: string
): PaginatorOptions => {
  const element = document.getElementById(selector) as HTMLInputElement;
  if (!element) return null;
  const value = element.value;
  if (!value) return null;
  try {
    return JSON.parse(value) as PaginatorOptions;
  } catch (error) {
    console.error("Failed to get initial paginator options", error);
    return null;
  }
};

const detectPickTranslatorModalOpen = (): boolean => {
  const modal = document.getElementById("translator-job-create-modal");
  if (!modal) {
    return false;
  }
  return (modal as HTMLDialogElement).open;
};

type TranslationViewMode = "raw_text" | "file";

type Props = {
  sourceText?: string;
  targetText?: string;
  sourceFilePath?: string;
  targetFilePath?: string;
  noTranslateCells?: {
    row: number;
    column: number;
  }[];
  sourceLanguage: string;
  targetLanguage: string;
  regexId?: number;
  modelId?: number;
  modelName?: string;
  customerId?: number;
  translatorId?: number;
  showAdminControls?: boolean;
  segmentationInputId?: string;
  segmentRegexInputId?: string;
  disabledSegmentTagRuleIdsInputId?: string;
  segmentationPaginatorInputId?: string;
  jobId?: number;
};

export const TranslationView: React.FC<Props> = ({
  sourceText,
  targetText,
  sourceFilePath,
  targetFilePath,
  noTranslateCells,
  sourceLanguage,
  targetLanguage,
  regexId: initialRegexId,
  modelId,
  modelName,
  customerId,
  translatorId,
  showAdminControls = false,
  segmentationInputId,
  segmentRegexInputId,
  disabledSegmentTagRuleIdsInputId,
  segmentationPaginatorInputId,
  jobId,
}) => {
  const mode: TranslationViewMode = useMemo(() => {
    if (sourceFilePath) {
      return "file";
    }
    if (sourceText) {
      return "raw_text";
    }
    console.error(
      `Was not able to determine mode from file path: ${sourceFilePath} or source text: ${sourceText}`
    );
    return "raw_text";
  }, [sourceFilePath, sourceText]);

  if (detectPickTranslatorModalOpen()) {
    return null;
  }
  const { data: auth } = useGetAuthQuery();
  const mountedRef = useRef(true);
  const controlInputDisabledTaggingRules = useRef<HTMLInputElement>();
  const controlInputSegmentationPaginator = useRef<HTMLInputElement>();

  const [paginatorOptions, setPaginatorStateOptions] = useState<
    PaginatorOptions
  >(null);
  const segmentationItems = useSegmentation(
    jobId,
    segmentationInputId,
    noTranslateCells
  );
  const segmentRegex = useSegmentRegex(initialRegexId, segmentRegexInputId);
  const [disabledTaggingRules, setDisabledTaggingRulesState] = useState<
    number[]
  >([]);

  const setDisabledTaggingRules: typeof setDisabledTaggingRulesState = useCallback(
    (newState: number[] | ((prev: number[]) => number[])) => {
      if (!mountedRef.current) return;
      setDisabledTaggingRulesState((prevItems) => {
        const newItems =
          typeof newState === "function" ? newState(prevItems) : newState;
        if (controlInputDisabledTaggingRules.current) {
          controlInputDisabledTaggingRules.current.value = JSON.stringify(
            newItems
          );
        }
        return newItems;
      });
    },
    []
  );
  const setPaginatorOptions: typeof setPaginatorStateOptions = useCallback(
    (
      newState:
        | PaginatorOptions
        | ((prev: PaginatorOptions) => PaginatorOptions)
    ) => {
      if (!mountedRef.current) return;
      setPaginatorStateOptions((prevItems) => {
        const newItems =
          typeof newState === "function" ? newState(prevItems) : newState;
        if (controlInputSegmentationPaginator.current) {
          controlInputSegmentationPaginator.current.value = JSON.stringify(
            newItems
          );
        }
        return newItems;
      });
    },
    []
  );
  useLayoutEffect(() => {
    if (!disabledSegmentTagRuleIdsInputId) return;
    const input = document.getElementById(disabledSegmentTagRuleIdsInputId);
    if (input) {
      controlInputDisabledTaggingRules.current = input as HTMLInputElement;
      const disabledIds = getInitialDisabledSegmentTagRuleIds(
        disabledSegmentTagRuleIdsInputId
      );
      setDisabledTaggingRules(disabledIds);
    }
  }, [disabledSegmentTagRuleIdsInputId]);

  useLayoutEffect(() => {
    if (!segmentationPaginatorInputId) return;
    const input = document.getElementById(segmentationPaginatorInputId);
    if (input) {
      controlInputSegmentationPaginator.current = input as HTMLInputElement;
      const paginator = getInitialSegmentationPaginatorOptions(
        segmentationPaginatorInputId
      );
      setPaginatorOptions(paginator);
    }
  }, [segmentationPaginatorInputId]);

  const allowedToShowAdminControls = useMemo(() => {
    if (!auth) {
      return false;
    }
    return auth.user.is_staff && showAdminControls;
  }, [auth, showAdminControls]);

  useLayoutEffect(() => {
    const abortController = new AbortController();
    window.addEventListener("keydown", segmentTextsFocusHandler, {
      signal: abortController.signal,
    });

    return () => abortController.abort();
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (mountedRef.current) {
      if (mode == "file") {
        segmentationItems.segmentFromRegex(
          segmentRegex.regex,
          sourceFilePath,
          targetFilePath
        );
      } else {
        segmentationItems.segmentFromRegex(
          segmentRegex.regex,
          sourceText,
          targetText
        );
      }
    }
  }, [
    segmentRegex.regex,
    sourceFilePath,
    targetFilePath,
    targetText,
    sourceText,
    mode,
  ]);

  useEffect(() => {
    if (!mountedRef.current || segmentationItems.isSegmenting) return;
    noTranslateCells?.forEach((noTranslateCell) => {
      segmentationItems.items.forEach((item, index) => {
        if (
          item?.file_info?.row === noTranslateCell.row &&
          item?.file_info?.column === noTranslateCell.column
        ) {
          segmentationItems.toggleLockAtIndex(index);
        }
      });
    });
  }, [noTranslateCells, segmentationItems.isSegmenting, segmentRegex.regex]);

  const onCommitTranslationToMemory = async (index: number): Promise<void> => {
    const textSegmentToCommit = segmentationItems.getAtIndex(index);
    if (!textSegmentToCommit) {
      console.error("No text segment to commit");
      store.dispatch(
        setDjangoToastOpen({
          content: "Found no text segment to commit",
          appearance: NotificationAppearance.WARNING,
        })
      );
      return Promise.resolve();
    }
    if (textSegmentToCommit?.translation_memory_id) {
      store.dispatch(
        setDjangoToastOpen({
          content: "Segment already in translation memory",
          appearance: NotificationAppearance.WARNING,
        })
      );
      return Promise.resolve();
    }
    if (
      !textSegmentToCommit?.source_text ||
      !textSegmentToCommit?.target_text
    ) {
      console.error("No source or target text to commit");
      store.dispatch(
        setDjangoToastOpen({
          content: "No source or target text to commit",
          appearance: NotificationAppearance.WARNING,
        })
      );
      return;
    }

    await addTranslationMemory(
      textSegmentToCommit.source_text,
      textSegmentToCommit.target_text,
      sourceLanguage,
      targetLanguage,
      customerId,
      translatorId,
      modelName,
      modelId
    )
      .then((data) => {
        store.dispatch(
          setDjangoToastOpen({
            content: "Segment added to translation memory",
            appearance: NotificationAppearance.SUCCESS,
          })
        );
        segmentationItems.setAtIndex(index, {
          translation_memory_id: data.id,
          translation_memory_similarity: 1.0,
        });
        changeWordCountInput(
          textSegmentToCommit?.source_words || 0,
          "subtract"
        );
      })
      .catch((error) => {
        store.dispatch(
          setDjangoToastOpen({
            content: "Failed to add segment to translation memory",
            appearance: NotificationAppearance.ERROR,
          })
        );
        console.error(error);
      });
  };

  const handleToggleLockSegmentAndFocusNext = (index: number): void => {
    const shouldFocusNext =
      !segmentationItems.getAtIndex(index).locked && allowedToShowAdminControls;
    segmentationItems.toggleLockAtIndex(index);
    if (shouldFocusNext) {
      focusSegment(getNextFocusableSegment(index));
    }
  };

  const handleTranslationMemoryClick = (tm: TranslationMemory): void => {
    const indexToInsert = segmentationItems.focusHandler.index;
    const segment = segmentationItems.focusHandler.segment();
    if (segment?.translation_memory_id === tm.id) {
      remainFocusOnSegment(indexToInsert);
      return;
    }
    const oldSimilarity = segment?.translation_memory_similarity || 0.0;
    if (tm.similarity >= 1.0 && !(oldSimilarity >= 1.0)) {
      changeWordCountInput(segment?.source_words, "subtract");
    } else if (tm.similarity < 1.0 && oldSimilarity >= 1.0) {
      changeWordCountInput(segment?.source_words, "add");
    }
    segmentationItems.setAtIndex(indexToInsert, {
      target_text: tm.target_text,
      translation_memory_id: tm.id,
      translation_memory_similarity: tm.similarity,
    });
    remainFocusOnSegment(indexToInsert);
  };

  const translationViewSegmentRowProps: Omit<
    TranslationViewSegmentRowProps,
    "index" | "segmentationItem"
  > = {
    focusedSegmentIndex: segmentationItems.focusHandler.index,
    showLockIcons: allowedToShowAdminControls,
    onCommitTranslationToMemory: onCommitTranslationToMemory,
    onChangeSegment: (index, value) => {
      const segment = segmentationItems.getAtIndex(index);
      // If the segment no longer uses the TM and the old TM had a similarity of 1.0,
      // we need to add the removed source words back to the word count
      if (segment?.translation_memory_similarity >= 1.0) {
        changeWordCountInput(segment?.source_words, "add");
      }
      segmentationItems.setAtIndex(index, {
        target_text: value,
        translation_memory_id: null, // reset translation memory id
      });
    },
    onToggleLockSegment: (index): boolean => {
      const segment = segmentationItems.getAtIndex(index);
      if (!segment) {
        return false;
      }
      const noTranslateCell = noTranslateCells?.some(
        (noTranslateCell) =>
          noTranslateCell.row === segment.file_info.row &&
          noTranslateCell.column === segment.file_info.column
      );
      if (noTranslateCell) {
        store.dispatch(
          setDjangoToastOpen({
            additionalContent: `Row ${
              index + 1
            } marked as no translate by Customer`,
            content: "Can't be unlocked",
            appearance: NotificationAppearance.WARNING,
          })
        );
        return false;
      }
      return segmentationItems.toggleLockAtIndex(index);
    },
    onFocusSegment: (index) => {
      segmentationItems.focusHandler.setIndex(index);
    },
    onCtrlL: (index) => {
      if (allowedToShowAdminControls) {
        handleToggleLockSegmentAndFocusNext(index);
      }
    },
    onCtrlEnter: (index) => {
      onCommitTranslationToMemory(index);
      focusSegment(getNextFocusableSegment(index));
    },
    disabledTaggingRules,
  };

  return (
    <div
      className="tw-flex tw-flex-col tw-h-full tw-overflow-hidden"
      data-testid="translation-view-container"
    >
      <div data-testid="translation-view-admin-panel">
        <TranslationViewAdminPanel
          allowedToShowAdminControls={allowedToShowAdminControls}
          regexOptions={segmentRegex.regexOptions}
          setRegexId={segmentRegex.setRegexId}
          regexId={segmentRegex.regexId}
          addNewRegex={segmentRegex.addRegex}
          disabledTaggingRules={disabledTaggingRules}
          setDisabledTaggingRules={setDisabledTaggingRules}
          segmentationItems={segmentationItems.items}
          setSegmentationItems={segmentationItems.setMultiple}
          sourceLanguage={sourceLanguage}
          targetLanguage={targetLanguage}
        />
      </div>
      {segmentationItems.isSegmenting ? (
        <div
          className="tw-text-center tw-animate-pulse"
          data-testid="translation-view-segmenting"
        >
          <Spinner />
        </div>
      ) : (
        <TranslationViewTranslationMemoryLookups
          focusedSegmentIndex={segmentationItems.focusHandler.index}
          focusedSegment={segmentationItems.focusHandler.segment()}
          sourceLanguage={sourceLanguage}
          targetLanguage={targetLanguage}
          handleTranslationMemoryClick={handleTranslationMemoryClick}
        />
      )}
      <div className="txu-divider tw-invisible"></div>
      <TranslationViewSegmentTable
        sourceLanguage={sourceLanguage}
        targetLanguage={targetLanguage}
        segmentationItems={segmentationItems.items}
        isFetchingRegex={false}
        isSegmenting={segmentationItems.isSegmenting}
        counts={segmentationItems.counts}
        paginatorOptions={paginatorOptions}
        onChangePaginator={setPaginatorOptions}
        showCellPaginator={mode == "file"}
        {...translationViewSegmentRowProps}
      />
    </div>
  );
};
